import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Save, ArrowRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAppStore } from '@/lib/store';
import type { Chapter } from '@/lib/store';
import { useMobile } from '@/hooks/use-mobile';
import { WritingQuote, writingQuotes } from './WritingQuote';
import { EditorToolbar } from './EditorToolbar';
import { BookMode } from './BookMode';
import { MobileFormatBar } from './MobileFormatBar';
import { TypewriterMode } from './TypewriterMode';
import { checkGrammar, type GrammarIssue, getSpellingSuggestions } from '@/lib/grammar';
import { GrammarPanel } from './GrammarPanel';
import { GrammarOverlay } from './GrammarOverlay';
import { DefinitionPopup } from './DefinitionPopup';
import { getDefinition, type Definition } from '@/lib/dictionary';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Typography from '@tiptap/extension-typography';
import { ExportModal } from './ExportModal';
import { PublishModal } from './PublishModal';
import { useWritingSession } from '@/hooks/useWritingSession';
import { SessionEndModal, FrictionCheckIn, FrictionNudgeToast } from '@/components/SyncModals';
import { useTypewriterAudio } from '@/hooks/useTypewriterAudio';
import { FloatingToolbar } from './FloatingToolbar';
import { MarginMenu } from './MarginMenu';
import { WordCountArc } from './WordCountArc';
import { FixedToolbar } from './FixedToolbar';
import { AnimatePresence, motion } from 'framer-motion';
import { BetaReaderModal } from '../BetaReaderModal';

interface ManuscriptEditorProps {
  chapter: Chapter;
}

const useReducedMotion = () => {
  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mediaQuery.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);
  return reducedMotion;
};

export function ManuscriptEditor({ chapter }: ManuscriptEditorProps) {
  const {
    updateChapterContent,
    updateChapter,
    activeNovelId,
    novels,
    sidebarMode,
    theme,
    setTheme,
    setDraggedBubble,
    draggedBubbleId,
    themeProfile,
    settings,
    recordDailyWords,
  } = useAppStore();

  const {
    sessionSummary,
    showFrictionCheck,
    frictionNudge,
    beginSession,
    recordChapterEdit,
    endSession,
    saveBreadcrumbNote,
    dismissSummary,
    recordFriction,
    dismissFrictionNudge,
  } = useWritingSession();

  const [content, setContent] = useState(chapter.content);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [uiVisible, setUiVisible] = useState(true);
  const [title, setTitle] = useState(chapter.title);
  const [showQuote, setShowQuote] = useState(true);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);

  const [streakMultiplier, setStreakMultiplier] = useState(1);
  const [isTyping, setIsTyping] = useState(false);
  const [momentum, setMomentum] = useState(0);
  const [delightMessage, setDelightMessage] = useState<string | null>(null);
  const [showBreadcrumbPrompt, setShowBreadcrumbPrompt] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState('');
  const [sessionWordCount, setSessionWordCount] = useState(0);
  const [lastWordCount, setLastWordCount] = useState(chapter.wordCount || 0);
  const [breadcrumbDismissed, setBreadcrumbDismissed] = useState(false);

  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const momentumIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [, setSpeedMetrics] = useState<number[]>([]);
  const [editorMode, setEditorMode] = useState<'standard' | 'typewriter' | 'book'>('standard');
  const [currentPage, setCurrentPage] = useState(0);
  const [grammarIssues, setGrammarIssues] = useState<GrammarIssue[]>([]);
  const [showGrammarPanel, setShowGrammarPanel] = useState(false);

  const [selectedDefinition, setSelectedDefinition] = useState<Definition | null>(null);
  const [selectedWord, setSelectedWord] = useState<string>('');
  const [selectedRange, setSelectedRange] = useState<{ start: number; end: number } | null>(null);
  const [spellingSuggestions, setSpellingSuggestions] = useState<string[]>([]);
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [focusMode, setFocusMode] = useState(false);
  const [showBetaReaderModal, setShowBetaReaderModal] = useState(false);
  const { playSound } = useTypewriterAudio();

  const editorRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const uiTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const quoteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeNovel = novels.find(n => n.id === activeNovelId);
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const { isMobile } = useMobile();
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (!showQuote) return;
    quoteTimerRef.current = setInterval(() => {
      setCurrentQuoteIndex((prev) => (prev + 1) % writingQuotes.length);
    }, 30000);
    return () => {
      if (quoteTimerRef.current) clearInterval(quoteTimerRef.current);
    };
  }, [showQuote]);

  useEffect(() => {
    return () => {
      endSession(chapter.id);
    };
  }, [chapter.id]);

  // Track last processed chapter to avoid unnecessary updates
  const lastChapterRef = useRef(chapter.id);
  
  useEffect(() => {
    // Only reset content when switching to a different chapter
    if (lastChapterRef.current !== chapter.id) {
      setContent(chapter.content);
      setTitle(chapter.title);
      setCurrentPage(0);
      lastChapterRef.current = chapter.id;
    }
  }, [chapter.id]); // Only depend on chapter.id, not content/title

  useEffect(() => {
    if (isMobile || editorMode === 'typewriter') return;
    const resetTimer = () => {
      setUiVisible(true);
      if (uiTimerRef.current) clearTimeout(uiTimerRef.current);
      uiTimerRef.current = setTimeout(() => {
        setUiVisible(false);
      }, 4000);
    };
    window.addEventListener('keydown', resetTimer);
    window.addEventListener('mousemove', resetTimer);
    resetTimer();
    return () => {
      window.removeEventListener('keydown', resetTimer);
      window.removeEventListener('mousemove', resetTimer);
      if (uiTimerRef.current) clearTimeout(uiTimerRef.current);
    };
  }, [isMobile, editorMode]);

  const handleScroll = useCallback(() => {
    if (editorRef.current && overlayRef.current) {
      overlayRef.current.scrollTop = editorRef.current.scrollTop;
    }
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      ...(settings.smartTypographyEnabled ? [Typography] : [])
    ],
    content: chapter.content,
    onUpdate: ({ editor, transaction }) => {
      if (!transaction.docChanged) return;
      const html = editor.getHTML();
      const plainText = editor.getText();
      setContent(html);
      updateChapterContent(chapter.id, html);
      beginSession();
      recordChapterEdit(chapter.id);

      setIsTyping(true);
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      typingTimerRef.current = setTimeout(() => {
        setIsTyping(false);
        setStreakMultiplier(1);
      }, 15000);

      const charLength = plainText.length;
      if (charLength % 50 === 0 && isTyping) {
        setStreakMultiplier(prev => Math.min(prev + 1, 10));
      }

      if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);

      const currentWords = plainText.trim().split(/\s+/).filter(w => w.length > 0).length;
      if (currentWords > lastWordCount) {
        const addedWords = currentWords - lastWordCount;
        setSessionWordCount(prev => prev + addedWords);
        setLastWordCount(currentWords);
        recordDailyWords(addedWords);
        if (settings.microDelightsEnabled && !settings.quietMode && (sessionWordCount + addedWords) % 50 === 0) {
          setDelightMessage(`+${sessionWordCount + addedWords} words! 🔥`);
          setTimeout(() => setDelightMessage(null), 3000);
        }
      } else if (currentWords < lastWordCount) {
        setLastWordCount(currentWords);
      }

      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        setLastSaved(new Date());
      }, 1000);
    },
    onTransaction: ({ transaction }) => {
      if (transaction.docChanged) {
        if (settings.momentumMeterEnabled) {
          setSpeedMetrics((prev: number[]) => [...prev, Date.now()].slice(-20));
        }

        // Typewriter Scrolling logic
        if (settings.typewriterScrollingEnabled && editorRef.current) {
          const { view } = editor;
          const { state } = view;
          const { selection } = state;

          // Use requestAnimationFrame to ensure the DOM has updated
          requestAnimationFrame(() => {
            const coords = view.coordsAtPos(selection.from);
            const container = editorRef.current?.closest('.overflow-y-auto') || editorRef.current;
            if (container) {
              const containerRect = container.getBoundingClientRect();
              const relativeY = coords.top - containerRect.top;
              const targetY = containerRect.height / 2.5; // Slightly above center for better visibility
              const diff = relativeY - targetY;

              if (Math.abs(diff) > 20) {
                container.scrollBy({ top: diff, behavior: 'smooth' });
              }
            }
          });
        }

        const lastStep = transaction.steps[transaction.steps.length - 1];
        if (lastStep && settings.typewriterSoundsEnabled) {
          const stepJSON = lastStep.toJSON();
          if (stepJSON.stepType === 'replace') {
            const insertedText = stepJSON.slice?.content?.[0]?.text || '';
            const isDeletion = !insertedText && stepJSON.from !== stepJSON.to;

            if (isDeletion) {
              playSound('delete');
            } else if (insertedText === ' ') {
              playSound('space');
            } else if (insertedText.includes('\n')) {
              playSound('enter');
              if (settings.soundProfile === 'typewriter') {
                setTimeout(() => playSound('ding'), 150);
              }
            } else if (insertedText.length > 0) {
              playSound('key');
            }
          }
        }
      }
    },
    editorProps: {
      attributes: {
        class: cn(
          'w-full outline-none bg-transparent max-w-none text-xl relative z-10 p-0 manuscript-editor-content',
          focusMode && 'focus-mode-active'
        ),
        spellcheck: 'false',
      },
    },
    autofocus: 'end',
  });

  useEffect(() => {
    if (editorMode === 'standard' && editorRef.current) {
      editorRef.current.focus();
    }
  }, [editorMode]);

  useEffect(() => {
    if (!settings.momentumMeterEnabled) return;
    momentumIntervalRef.current = setInterval(() => {
      const now = Date.now();
      setSpeedMetrics((prev: number[]) => {
        const recent = prev.filter((t: number) => now - t < 5000);
        const m = Math.min((recent.length / 20) * 100, 100);
        setMomentum(m);
        return recent;
      });
    }, 1000);
    return () => {
      if (momentumIntervalRef.current) clearInterval(momentumIntervalRef.current);
    };
  }, [settings.momentumMeterEnabled]);

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  useEffect(() => {
    if (!settings.grammarEnabled) {
      setGrammarIssues([]);
      return;
    }
    const timer = setTimeout(() => {
      const bubbleMap = activeNovel?.bubbles?.map(b => ({ id: b.id, name: b.name, type: b.type })) || [];
      const customWordList = activeNovel?.customWords?.map(cw => cw.word) || [];
      const plainText = editor ? editor.getText() : content;
      setGrammarIssues(checkGrammar(plainText, bubbleMap, customWordList));
    }, 1500);
    return () => clearTimeout(timer);
  }, [content, editor, settings.grammarEnabled, activeNovel?.bubbles]);

  const newbornPositionOffset = useCallback((offset: number) => {
    if (!editor) return 1;
    const text = editor.getText();
    const textBefore = text.slice(0, offset);
    const newlineCount = (textBefore.match(/\n/g) || []).length;
    return 1 + newlineCount;
  }, [editor]);

  const applyFix = useCallback((issue: GrammarIssue, chosenSuggestion?: string) => {
    if (!editor) return;
    const suggestionToApply = chosenSuggestion || issue.suggestion;
    const from = issue.start + newbornPositionOffset(issue.start);
    const to = issue.end + newbornPositionOffset(issue.end);
    editor.chain().focus().insertContentAt({ from, to }, suggestionToApply).run();
    setLastSaved(new Date());
  }, [editor, newbornPositionOffset]);

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setContent(newContent);
    updateChapterContent(chapter.id, newContent);
    setIsTyping(true);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      setIsTyping(false);
      setStreakMultiplier(1);
    }, 15000);
    if (pauseTimerRef.current) clearTimeout(pauseTimerRef.current);
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => setLastSaved(new Date()), 1000);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setTitle(newTitle);
    updateChapter(chapter.id, { title: newTitle });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && editorMode === 'typewriter') setEditorMode('standard');
  };

  const handleDoubleClick = async (e: React.MouseEvent) => {
    if (!editor) return;
    setTimeout(async () => {
      const { from, to, empty } = editor.state.selection;
      if (empty) return;
      const word = editor.state.doc.textBetween(from, to, ' ').trim();
      if (word && word.length > 1) {
        setSelectedWord(word);
        setSelectedRange({ start: from, end: to });
        setPopupPosition({ x: e.clientX, y: e.clientY });
        const [def, sugs] = await Promise.all([getDefinition(word), getSpellingSuggestions(word)]);
        setSelectedDefinition(def);
        setSpellingSuggestions(sugs);
      }
    }, 50);
  };

  const handleReplace = (newWord: string) => {
    if (!selectedRange || !editor) return;
    editor.chain().focus().insertContentAt({ from: selectedRange.start, to: selectedRange.end }, newWord).run();
    setSelectedRange(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (!draggedBubbleId || !activeNovel || !editor) return;
    const bubble = activeNovel.bubbles.find(b => b.id === draggedBubbleId);
    if (!bubble) return;
    const coords = { left: e.clientX, top: e.clientY };
    const pos = editor.view.posAtCoords(coords);
    if (!pos) return;
    editor.chain().focus().insertContentAt(pos.pos, bubble.content ? `${bubble.name}: ${bubble.content}` : bubble.name).run();
    setDraggedBubble(null);
  };

  const handleDragOver = (e: React.DragEvent) => e.preventDefault();

  const CHARS_PER_PAGE = 1200;
  const totalPages = Math.max(1, Math.ceil(content.length / CHARS_PER_PAGE));
  const getPageContent = useCallback((pageNum: number) => {
    const startChar = pageNum * CHARS_PER_PAGE;
    return content.slice(startChar, startChar + CHARS_PER_PAGE);
  }, [content]);

  const getLeftPageContent = useCallback(() => {
    if (currentPage === 0) return { isTitlePage: true, content: '' };
    return { isTitlePage: false, content: getPageContent(currentPage - 1) };
  }, [currentPage, getPageContent]);

  const getRightPageContent = useCallback(() => ({ content: getPageContent(currentPage) }), [currentPage, getPageContent]);

  const handlePageTurn = (direction: 'next' | 'prev') => {
    if (direction === 'next' && currentPage < totalPages - 1) setCurrentPage(p => p + 1);
    else if (direction === 'prev' && currentPage > 0) setCurrentPage(p => p - 1);
  };

  const wordCount = content.trim().split(/\s+/).filter(w => w.length > 0).length;
  const charCount = content.length;
  const currentQuote = writingQuotes[currentQuoteIndex];

  const themeColors = {
    bg: themeProfile.background,
    bgSecondary: themeProfile.paperSurface,
    bgPaper: themeProfile.paperSurface,
    text: themeProfile.text,
    textMuted: themeProfile.accent + '99',
    textSecondary: themeProfile.accent,
    border: themeProfile.border,
    sage: themeProfile.accent,
    quoteBg: themeProfile.accent + '15',
    bookBg: themeProfile.background,
    pageBg: themeProfile.paperSurface,
    spineBg: `linear-gradient(to right, ${themeProfile.border}, ${themeProfile.paperSurface}, ${themeProfile.border})`,
  };

  return (
    <div className="h-full flex flex-col relative overflow-hidden transition-colors duration-500" style={{ backgroundColor: editorMode === 'book' ? themeColors.bookBg : themeColors.bgPaper }}>
      {isMobile ? (
        <>
          <EditorToolbar
            isDark={isDark} setTheme={setTheme}
            editorMode={editorMode} setEditorMode={setEditorMode}
            wordCount={wordCount} themeColors={themeColors}
            isMobile={true} onExport={() => setShowExportModal(true)} onPublish={() => setShowPublishModal(true)}
          />

          {settings.grammarEnabled && grammarIssues.length > 0 && (
            <div className="px-3 py-1.5 text-[10px] font-medium flex items-center gap-2 border-b" style={{ backgroundColor: themeColors.bgSecondary, color: themeColors.textMuted, borderColor: themeColors.border }}>
              <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
              {grammarIssues.length} suggesting{grammarIssues.length !== 1 ? 's' : ''} to improve your flow
            </div>
          )}

          {showQuote && editorMode !== 'typewriter' && (
            <WritingQuote quote={currentQuote} onClose={() => setShowQuote(false)} themeColors={themeColors} className="rounded-none border-t-0" />
          )}

          <div className="flex-1 overflow-hidden relative">
            {editorMode === 'typewriter' ? (
              <TypewriterMode
                content={content} handleInput={handleInput} handleKeyDown={handleKeyDown}
                setEditorMode={setEditorMode} isDark={isDark} setTheme={setTheme}
                themeColors={themeColors} wordCount={wordCount} charCount={charCount}
                lastSaved={lastSaved} isMobile={true} grammarIssues={grammarIssues}
                setShowGrammarPanel={setShowGrammarPanel}
              />
            ) : editorMode === 'book' ? (
              <BookMode
                content={content} title={title} currentPage={currentPage} totalPages={totalPages}
                handlePageTurn={handlePageTurn} leftPage={getLeftPageContent()} rightPage={getRightPageContent()}
                themeColors={themeColors} isDark={isDark} reducedMotion={reducedMotion} isMobile={true} chapterId={chapter.id}
              />
            ) : (
              <div className="h-full overflow-y-auto px-4 py-4" style={{ backgroundColor: themeColors.bg }}>
                <div className="text-center mb-6">
                  <input type="text" value={title} onChange={handleTitleChange} className="w-full text-center text-xl font-semibold bg-transparent border-none outline-none" style={{ color: themeColors.text }} placeholder="Chapter Title" />
                  <div className="w-12 h-px mx-auto mt-3" style={{ backgroundColor: themeColors.border }} />
                </div>
                <FloatingToolbar editor={editor} />
                <MarginMenu editor={editor} />
                <div className="relative tiptap-container" ref={editorRef} onScroll={handleScroll}>
                  {settings.fixedToolbarEnabled && (
                    <FixedToolbar editor={editor} themeColors={themeColors} className="z-30 sticky top-0" />
                  )}
                  <EditorContent
                    editor={editor} onKeyDown={handleKeyDown} onDrop={handleDrop} onDragOver={handleDragOver}
                    onDoubleClick={handleDoubleClick as any}
                    style={{ color: themeColors.text, lineHeight: '1.8', letterSpacing: '0.01em', minHeight: '70vh', padding: '4px 0' }}
                  />
                  <style>{`
                    .tiptap-container .ProseMirror { max-width: 65ch; margin: 0 auto; outline: none; }
                    .tiptap-container .ProseMirror p { margin: 1.25em 0; transition: opacity 0.3s ease; animation: paragraphFadeIn 0.8s ease-out; }
                    @keyframes paragraphFadeIn { from { opacity: 0; transform: translateY(5px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
                    .focus-mode-active .ProseMirror p:not(.ProseMirror-focused) { opacity: 0.15; }
                    .manuscript-editor-content .ProseMirror p.is-empty::before { color: ${themeColors.textMuted}; content: attr(data-placeholder); float: left; height: 0; pointer-events: none; font-style: italic; }
                  `}</style>
                  <div ref={overlayRef} className="absolute inset-0 overflow-hidden pointer-events-none z-20 max-w-[65ch] mx-auto">
                    <GrammarOverlay
                      text={editor ? editor.getText() : content}
                      issues={grammarIssues}
                      style={{
                        lineHeight: '1.8',
                        letterSpacing: '0.01em',
                        fontSize: '1.15rem',
                        padding: '4px 0',
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        marginTop: settings.fixedToolbarEnabled ? '5.25em' : '1.25em',
                        fontFamily: 'DM Sans, sans-serif'
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {isMobile && editorMode === 'standard' && <MobileFormatBar editor={editor} />}

          <footer className="flex items-center justify-between px-3 py-2 relative" style={{ backgroundColor: themeColors.bgSecondary, borderTop: `1px solid ${themeColors.border}` }}>
            {settings.momentumMeterEnabled && (
              <div className="absolute top-0 left-0 h-0.5 bg-gradient-to-r from-transparent via-amber-500 to-transparent transition-all duration-300" style={{ width: `${momentum}%`, opacity: momentum > 5 ? 1 : 0 }} />
            )}
            <div className="flex items-center gap-3 text-xs" style={{ color: themeColors.textMuted }}>
              <span>{wordCount.toLocaleString()} {wordCount === 1 ? 'word' : 'words'}</span>
              {settings.streakEnabled && streakMultiplier > 1 && <span className="flex items-center gap-1 font-bold text-orange-500 animate-pulse">🔥 {streakMultiplier}x</span>}
            </div>
            <div className="flex items-center gap-2">
              {lastSaved && (
                <span className="text-[10px] flex items-center gap-1 opacity-60 animate-pulse" style={{ color: themeColors.sage }}>
                  <Save className="w-3 h-3" />
                  Saved
                </span>
              )}
              {settings.breadcrumbsEnabled && <button onClick={() => setShowBreadcrumbPrompt(true)} className="text-[10px] px-2 py-1 rounded-md transition-colors hover:bg-black/5 dark:hover:bg-white/5" style={{ color: themeColors.textMuted }}>Hook</button>}
            </div>
            <div className="scale-75 origin-right">
              <WordCountArc
                current={sessionWordCount}
                target={settings.dailyWordGoal || 1000}
                momentum={momentum}
              />
            </div>
          </footer>
        </>
      ) : (
        <>
          {editorMode === 'typewriter' ? (
            <TypewriterMode
              content={content} handleInput={handleInput} handleKeyDown={handleKeyDown}
              setEditorMode={setEditorMode} isDark={isDark} setTheme={setTheme}
              themeColors={themeColors} wordCount={wordCount} charCount={charCount}
              lastSaved={lastSaved} grammarIssues={grammarIssues} setShowGrammarPanel={setShowGrammarPanel}
            />
          ) : editorMode === 'book' ? (
            <div className="h-full flex flex-col relative overflow-hidden">
              <header className={cn('transition-all duration-500 px-6 py-3', uiVisible ? 'opacity-100' : 'opacity-0 pointer-events-none')} style={{ backgroundColor: themeColors.bgSecondary, borderBottom: `1px solid ${themeColors.border}` }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <EditorToolbar
                      isDark={isDark} setTheme={setTheme} editorMode={editorMode} setEditorMode={setEditorMode}
                      wordCount={wordCount} themeColors={themeColors} onExport={() => setShowExportModal(true)} onPublish={() => setShowPublishModal(true)}
                    />
                    <span className="text-sm font-medium" style={{ color: themeColors.textMuted }}>{title}</span>
                  </div>
                  <div className="flex items-center gap-6 text-sm" style={{ color: themeColors.textMuted }}>
                    <span>Page {currentPage * 2 + (getLeftPageContent().isTitlePage ? 1 : 0)}-{currentPage * 2 + 2} of {totalPages * 2}</span>
                    <span>{wordCount.toLocaleString()} words</span>
                  </div>
                </div>
              </header>
              <BookMode
                content={content} title={title} currentPage={currentPage} totalPages={totalPages}
                handlePageTurn={handlePageTurn} leftPage={getLeftPageContent()} rightPage={getRightPageContent()}
                themeColors={themeColors} isDark={isDark} reducedMotion={reducedMotion} activeNovel={activeNovel} chapterId={chapter.id}
              />
            </div>
          ) : (
            <>
              <header className={cn('sticky top-0 z-30 transition-all duration-500', uiVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none')} style={{ backgroundColor: themeColors.bgSecondary, borderBottom: `1px solid ${themeColors.border}` }}>
                <div className={cn('mx-auto px-8 py-3 transition-all duration-300', sidebarMode === 'pinned' ? 'max-w-3xl' : 'max-w-2xl')}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <input type="text" value={title} onChange={handleTitleChange} className="text-lg font-semibold bg-transparent border-none outline-none min-w-[200px]" style={{ color: themeColors.text }} />
                      {lastSaved && (
                        <div 
                          className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium border"
                          style={{ 
                            backgroundColor: `${themeProfile.accent}20`,
                            color: themeProfile.accent,
                            borderColor: `${themeProfile.accent}40`
                          }}
                          aria-live="polite"
                          aria-atomic="true"
                        >
                          <div 
                            className="w-1 h-1 rounded-full animate-pulse" 
                            style={{ backgroundColor: themeProfile.accent }}
                            aria-hidden="true"
                          />
                          SAVED • {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => setFocusMode(!focusMode)} 
                        className={cn("p-2 rounded-full transition-all duration-300 min-w-10 min-h-10 flex items-center justify-center", focusMode ? "bg-sage text-white shadow-lg" : "hover:bg-zinc-100 dark:hover:bg-zinc-800 text-zinc-400")} 
                        aria-label={focusMode ? "Exit focus mode" : "Enter focus mode"}
                        aria-pressed={focusMode}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" /></svg>
                      </button>
                      <EditorToolbar isDark={isDark} setTheme={setTheme} editorMode={editorMode} setEditorMode={setEditorMode} wordCount={wordCount} themeColors={themeColors} onExport={() => setShowExportModal(true)} onPublish={() => setShowPublishModal(true)} onBetaReaders={() => setShowBetaReaderModal(true)} />
                    </div>
                  </div>
                </div>
              </header>

              <main className="flex-1 overflow-y-auto cursor-text" onClick={() => editor?.commands.focus()} style={{ backgroundColor: themeColors.bgPaper }}>
                <div className="max-w-4xl mx-auto px-4 py-8 md:px-8 min-h-full">
                  <div className="mb-8 flex justify-between items-end gap-4 overflow-visible">
                    <div className="flex-1" />
                    <div className="flex-1 text-center">
                      <input type="text" value={title} onChange={handleTitleChange} className="chapter-title-input w-full text-center text-5xl font-bold bg-transparent border-none outline-none mb-4" style={{ color: themeColors.text, fontFamily: 'Lora, serif' }} />
                      <div className="w-16 h-0.5 mx-auto mb-12 opacity-20" style={{ backgroundColor: themeColors.text }} />
                    </div>
                    <div className="flex-1" />
                  </div>

                  {settings.fixedToolbarEnabled && (
                    <FixedToolbar editor={editor} themeColors={themeColors} className="mb-8 sticky top-0" style={{ zIndex: 'var(--z-sticky)' }} />
                  )}

                  <div className="relative min-h-[70vh] manuscript-paper-container">
                    {settings.floatingToolbarEnabled && <FloatingToolbar editor={editor} />}
                    {settings.marginMenuEnabled && <MarginMenu editor={editor} />}
                    <div className="tiptap-container" onScroll={handleScroll}>
                      <EditorContent editor={editor} onKeyDown={handleKeyDown} onDrop={handleDrop} onDragOver={handleDragOver} onDoubleClick={handleDoubleClick as any} className="relative" style={{ zIndex: 'var(--z-base)' }} />
                      <div ref={overlayRef} className="absolute inset-0 overflow-hidden pointer-events-none max-w-[65ch] mx-auto" style={{ zIndex: 1 }}>
                        <GrammarOverlay
                          text={editor ? editor.getText() : content}
                          issues={grammarIssues}
                          style={{
                            lineHeight: '1.8',
                            letterSpacing: '0.01em',
                            fontSize: '1.15rem',
                            padding: '80px 0',
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word',
                            fontFamily: 'DM Sans, sans-serif'
                          }}
                        />
                      </div>
                      <style>{`
                        .manuscript-paper-container { 
                          background-color: ${themeColors.bgPaper};
                          border-radius: 16px; 
                          padding: 80px 0;
                          position: relative;
                          overflow: hidden;
                        }
                        .focus-mode-overlay {
                          position: fixed;
                          inset: 0;
                          pointer-events: none;
                          z-index: var(--z-dropdown);
                          background: radial-gradient(circle at center, transparent 20%, rgba(0,0,0,0.05) 100%);
                          opacity: 0;
                          transition: opacity 1s ease;
                        }
                        @media (prefers-reduced-motion: reduce) {
                          .focus-mode-overlay {
                            transition: opacity 0.01ms !important;
                          }
                        }
                        .focus-mode-active ~ .focus-mode-overlay {
                          opacity: 1;
                        }
                        .tiptap-container .ProseMirror { 
                          max-width: 65ch; 
                          margin: 0 auto; 
                          font-family: 'DM Sans', sans-serif; 
                          font-size: 1.15rem; 
                          line-height: 1.8; 
                          color: ${themeColors.text};
                          outline: none;
                        }
                        .tiptap-container .ProseMirror p { 
                          margin: 1.25em 0; 
                          transition: all 0.6s cubic-bezier(0.2, 0, 0, 1); 
                          opacity: 1;
                          animation: paragraphAppear 0.8s ease-out;
                        }
                        @keyframes paragraphAppear {
                          from { opacity: 0; transform: translateY(10px); }
                          to { opacity: 1; transform: translateY(0); }
                        }
                        .focus-mode-active .ProseMirror p {
                          opacity: 0.08;
                          filter: blur(1.5px);
                          transform: scale(0.97);
                        }
                        .focus-mode-active .ProseMirror p.ProseMirror-focused { 
                          opacity: 1; 
                          filter: none; 
                          transform: scale(1.03);
                        }
                        @media (prefers-reduced-motion: reduce) {
                          .tiptap-container .ProseMirror p {
                            animation: none !important;
                            transition: opacity 0.01ms !important;
                          }
                          .focus-mode-active .ProseMirror p,
                          .focus-mode-active .ProseMirror p.ProseMirror-focused {
                            transition: opacity 0.01ms !important;
                            transform: none !important;
                          }
                        }
                        .manuscript-editor-content .ProseMirror p.is-empty::before {
                          color: ${themeColors.textMuted};
                          content: attr(data-placeholder);
                          float: left;
                          height: 0;
                          pointer-events: none;
                          font-style: italic;
                          opacity: 0.5;
                        }
                      `}</style>
                      <div className="absolute inset-x-0 top-0 h-full pointer-events-none z-20 max-w-[65ch] mx-auto">
                        <GrammarOverlay text={editor ? editor.getText() : content} issues={grammarIssues} style={{ lineHeight: '1.8', letterSpacing: '0.01em', fontSize: '1.25rem', padding: '4px 0', whiteSpace: 'pre-wrap', wordWrap: 'break-word', marginTop: '1.5em', opacity: 0.8 }} />
                      </div>
                      <div className="focus-mode-overlay" />
                    </div>
                  </div>
                </div>
                {settings.wordCountArcEnabled && (
                  <div className="fixed bottom-10 right-10 bg-white/5 hover:bg-white/10 dark:bg-black/5 dark:hover:bg-black/10 p-2 rounded-2xl backdrop-blur-md transition-all group" style={{ zIndex: 'var(--z-sticky)' }}>
                    <div className="absolute right-full mr-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                      {lastSaved && (
                        <span className="text-[10px] flex items-center gap-1 opacity-60 animate-pulse" style={{ color: themeProfile.accent }}>
                          <Save className="w-3 h-3" />
                          Autosaved
                        </span>
                      )}
                    </div>
                    <WordCountArc
                      current={sessionWordCount}
                      target={settings.dailyWordGoal || 1000}
                      momentum={momentum}
                    />
                  </div>
                )}
              </main>
            </>
          )}
        </>
      )}

      {showBreadcrumbPrompt && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in duration-300" style={{ zIndex: 'var(--z-modal)' }}>
          <div className="w-full max-w-lg mx-4 p-8 rounded-2xl shadow-2xl border" style={{ backgroundColor: themeColors.bgSecondary, borderColor: themeColors.border }}>
            <h3 className="text-xl font-bold mb-2" style={{ color: themeColors.text }}>Leave a Breadcrumb</h3>
            <p className="text-sm mb-6 opacity-70" style={{ color: themeColors.textMuted }}>Write one sentence to help your future self pick up the thread where you left off tonight.</p>
            <textarea
              className="w-full h-32 p-4 rounded-xl border bg-transparent resize-none focus:ring-2 focus:ring-offset-0 focus:outline-none transition-all"
              style={{ color: themeColors.text, borderColor: themeColors.border }}
              placeholder="What happens next...?"
              value={breadcrumb} onChange={(e) => setBreadcrumb(e.target.value)}
            />
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowBreadcrumbPrompt(false)} className="px-6 py-2 rounded-full text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5 transition-colors" style={{ color: themeColors.textMuted }}>Skip</button>
              <button
                onClick={() => {
                  saveBreadcrumbNote(breadcrumb, chapter.id);
                  updateChapter(chapter.id, { breadcrumb });
                  setShowBreadcrumbPrompt(false);
                }}
                className="px-8 py-2 rounded-full text-sm font-bold bg-sage text-white shadow-lg hover:scale-105 active:scale-95 transition-all"
              >Save Hook</button>
            </div>
          </div>
        </div>
      )}

      {settings.breadcrumbsEnabled && !breadcrumbDismissed && chapter.breadcrumb && editorMode === 'standard' && (
        <div 
          className="fixed bottom-24 left-1/2 -translate-x-1/2 w-full max-w-xl px-4 animate-in slide-in-from-bottom-4 duration-700"
          style={{ zIndex: 'var(--z-sticky)' }}
        >
          <div className="p-6 rounded-2xl border shadow-xl flex gap-4 backdrop-blur-md bg-opacity-95" style={{ backgroundColor: themeColors.bgSecondary, borderColor: themeColors.border }}>
            <div className="w-10 h-10 rounded-full bg-sage/20 flex items-center justify-center flex-shrink-0"><ArrowRight className="w-5 h-5 text-sage" /></div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-widest font-bold mb-1 opacity-50" style={{ color: themeColors.textMuted }}>Your last breadcrumb:</p>
              <p className="text-sm font-medium italic leading-relaxed" style={{ color: themeColors.text }}>"{chapter.breadcrumb}"</p>
            </div>
            <button 
              onClick={() => setBreadcrumbDismissed(true)} 
              className="self-start p-1 opacity-40 hover:opacity-100 transition-opacity min-w-8 min-h-8 flex items-center justify-center rounded" 
              style={{ color: themeColors.text }}
              aria-label="Dismiss breadcrumb"
            >×</button>
          </div>
        </div>
      )}

      <AnimatePresence>
        {delightMessage && (
          <motion.div 
            initial={reducedMotion ? false : { opacity: 0, y: 20, scale: 0.8 }} 
            animate={{ opacity: 1, y: 0, scale: 1 }} 
            exit={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.8 }} 
            transition={reducedMotion ? { duration: 0.01 } : undefined}
            className="fixed bottom-12 left-1/2 -translate-x-1/2 px-6 py-2.5 rounded-full font-bold shadow-2xl pointer-events-none" 
            style={{ backgroundColor: themeColors.sage, color: themeColors.bg, zIndex: 'var(--z-toast)' }}
            role="status"
            aria-live="polite"
          >{delightMessage}</motion.div>
        )}
      </AnimatePresence>

      {sessionSummary && (
        <SessionEndModal
          summary={sessionSummary}
          onSaveBreadcrumb={(hook) => {
            saveBreadcrumbNote(hook, chapter.id);
            updateChapter(chapter.id, { breadcrumb: hook });
          }}
          onDismiss={dismissSummary}
        />
      )}

      {showFrictionCheck && <FrictionCheckIn onSubmit={recordFriction} />}
      {frictionNudge && <FrictionNudgeToast message={frictionNudge} onDismiss={dismissFrictionNudge} />}

      {showGrammarPanel && (
        <GrammarPanel
          issues={grammarIssues}
          onFix={applyFix}
          onClose={() => setShowGrammarPanel(false)}
          themeColors={themeColors}
        />
      )}

      {showExportModal && (
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {showPublishModal && (
        <PublishModal
          onClose={() => setShowPublishModal(false)}
          chapter={chapter}
          novelId={activeNovelId || ''}
        />
      )}

      {showBetaReaderModal && activeNovelId && (
        <BetaReaderModal
          novelId={activeNovelId}
          isOpen={showBetaReaderModal}
          onClose={() => setShowBetaReaderModal(false)}
        />
      )}

      {selectedWord && (
        <DefinitionPopup
          word={selectedWord} definition={selectedDefinition} suggestions={spellingSuggestions}
          position={popupPosition} onClose={() => { setSelectedWord(''); setSelectedDefinition(null); setSelectedRange(null); setSpellingSuggestions([]); }}
          onReplace={handleReplace} themeColors={themeColors}
        />
      )}
    </div>
  );
}
